import { useParams, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import ChapterReader from "./components/ChapterReader";
import ChapterNav from "./components/ChapterNav";
import { comics, categories } from "@/mocks/comics";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const comic = comics.find((c) => c.id === slug);
  const chapterId = searchParams.get("chapter");
  const currentChapter = chapterId
    ? comic?.chapters.find((ch) => ch.id === chapterId)
    : comic?.chapters[0];

  if (!comic || !currentChapter) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              漫画未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              您查找的漫画不存在或已下架
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((cat) => cat.id === comic.category);

  const pageTitle = `${comic.title} - ${currentChapter.title} - 免费下拉式阅读 - 布丁漫画网`;
  const pageDescription = `${comic.title} ${currentChapter.title}，作者${comic.author}，通过布丁漫画网免费阅读蜜桃少年看下拉式漫画，在线下拉畅读全集中文故事，享受免费且流畅的漫画浏览体验。`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: comic.title,
    author: { "@type": "Person", name: comic.author },
    description: comic.description,
    genre: category?.name || "漫画",
    datePublished: comic.chapters[0]?.updatedAt,
    inLanguage: "zh-CN",
    provider: {
      "@type": "Organization",
      name: "布丁漫画网",
    },
  };

  const chapterList = comic.chapters.map((ch) => ({
    id: ch.id,
    title: ch.title,
    number: ch.number,
  }));

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(category
                  ? [{ label: category.name, href: `/category/${category.slug}` }]
                  : []),
                { label: comic.title },
                { label: currentChapter.title },
              ]}
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-0 max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex-1 min-w-0">
            <ChapterReader
              images={currentChapter.images}
              chapterTitle={currentChapter.title}
            />
            <ChapterNav
              comicId={comic.id}
              chapters={chapterList}
              currentChapterId={currentChapter.id}
            />
          </div>

          <aside className="hidden lg:block w-56 flex-shrink-0 ml-8">
            <div className="sticky top-24">
              <div className="bg-background-100 rounded-xl p-4 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 truncate">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{comic.author}</p>
                  </div>
                </div>

                <div className="space-y-1 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs mr-1 mb-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <h4 className="text-xs font-semibold text-foreground-700 mb-2">
                    章节列表
                  </h4>
                  <div className="max-h-80 overflow-y-auto space-y-0.5 pr-1 comic-grid-scrollbar">
                    {comic.chapters.map((ch) => (
                      <Link
                        key={ch.id}
                        to={`/article/${comic.id}?chapter=${ch.id}`}
                        className={`block px-3 py-2 rounded-lg text-xs transition-colors cursor-pointer ${
                          ch.id === currentChapter.id
                            ? "bg-primary-100 text-primary-700 font-medium"
                            : "text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {ch.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="lg:hidden max-w-2xl mx-auto px-4 mt-6">
          <div className="bg-background-100 rounded-xl p-4 border border-background-200/70">
            <h4 className="text-xs font-semibold text-foreground-700 mb-3">
              章节列表
            </h4>
            <div className="flex flex-wrap gap-2">
              {comic.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${comic.id}?chapter=${ch.id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                    ch.id === currentChapter.id
                      ? "bg-primary-100 text-primary-700 font-medium"
                      : "text-foreground-600 bg-background-200/70 hover:bg-background-200"
                  }`}
                >
                  {ch.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}